import { t } from 'i18next';
import moment from 'moment';
import React from 'react';
import Moment from 'react-moment';

import { EventLogClass, EventLogName, EventLogNameDis, EventLogSource, TicketUndeliveredLabel } from '../../../../../utils/common/constants';
import { formatEventLogDetails, getInvoiceFileTypeLabel } from 'app/utils/helpers/EventLogHelpers';
import { EventLogDetail, WorkerInfo } from '../../../models';
import { TicketSource } from 'app/utils/common/enum';

type Props = {
    data?: EventLogDetail[];
    worker?: WorkerInfo[];
    rtl?: boolean;
    sourceType?: number;
    customer: {
        id: string,
        fullName: string,
        phoneNumber: string,
        backupNumber?: string,
        cityName: string,
        address: string,
        statistics: {
            averageRating: number,
            totalCompleted: number,
            totalReceived: number,
        },
        nationalId?: string
    };
}

const PackageTimeLine: React.FC<Props> = ({ data, worker, sourceType, customer, rtl }) => {
    const useViewport = () => {
        const [width, setWidth] = React.useState(window.innerWidth);

        React.useEffect(() => {
            const handleWindowResize = () => setWidth(window.innerWidth);
            window.addEventListener("resize", handleWindowResize);
            return () => window.removeEventListener("resize", handleWindowResize);
        }, []);

        return { width };
    };
    const viewPort = useViewport();
    return (
        <div className={`${viewPort.width > 991 && viewPort.width < 1200 ? 'mh-900px' : 'mh-425px'} scroll`}>
            <div className={`${rtl ? "timeline-label-ar" : "timeline-label"} mt-5`} id='package-timeline'>
                {data && data.length > 0 && data.map((item, index) => (
                    <div className={`timeline-item ${item.source}`} key={index}>
                        <div className={`${rtl ? "timeline-label-ar" : "timeline-label"} fw-bolder text-gray-800 text-center small p-1`}>
                            <div><Moment date={item?.dateTime} locale={rtl ? 'ar-SA' : 'en-US'} format="HH:mm" /></div>
                            <div><Moment date={item?.dateTime} locale={rtl ? 'ar-SA' : 'en-US'} format="MM.DD.YY" /></div>
                        </div>
                        <div className='timeline-badge'>
                            <i className={'fa fa-genderless fs-1 ' + EventLogClass[item.event]}></i>
                        </div>

                        <div className='fw-bold timeline-content px-5 small'>
                            {[
                                EventLogName.Invoice_Uploaded,
                                EventLogName.Invoice_Updated,
                                EventLogName.Invoice_Removed
                            ].includes(item.event) ? (
                                <>
                                    <div>
                                        {EventLogNameDis[item.event] ? t(EventLogNameDis[item.event]) : item.event}
                                        {" - "}
                                        [{t(item.source)}] {t("VIEW_TICKET_MODAL.EVENT_NAME.AS")} {rtl ? item.userActionAr : item.userActionEn}
                                    </div>
                                    {item.ticketStatus && (
                                        <div className='text-muted'>
                                            {getInvoiceFileTypeLabel(item.ticketStatus, t)}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div>
                                    {item.source === EventLogSource.PWA ?
                                        <div>{EventLogNameDis[item.event] ? t(EventLogNameDis[item.event]) : item.event} - [{t(item.source)}] - {customer?.phoneNumber} - {customer?.fullName}</div>
                                        :
                                        <div>{EventLogNameDis[item.event] ? t(EventLogNameDis[item.event]) : item.event} - [{t(item.source)}] {rtl ? item.userActionAr : item.userActionEn}</div>
                                    }
                                </div>
                            )}

                            {item.event === EventLogName.Package_Ticket_Undelivered &&
                                <div className='text-muted'>
                                    {t(TicketUndeliveredLabel[item.ticketStatus])}
                                </div>
                            }

                            {(item.event === EventLogName.Payment_Method_Changed
                              || item.event === EventLogName.Payment_Pending
                              || item.event === EventLogName.Payment_Completed) && !!item.ticketStatus &&
                                <div className='text-muted'>
                                    {item.ticketStatus}
                                </div>
                            }

                            {(item.event === EventLogName.Payment_Pending
                              || item.event === EventLogName.Payment_Completed
                              || item.event === EventLogName.Ticket_Canceled) && !!item.details &&
                                <div className='text-muted'>
                                    {formatEventLogDetails(item.event, item.details, rtl)}
                                </div>
                            }

                            {item.event === EventLogName.Ticket_Reassigned &&
                                <div className='text-muted'>
                                    {t("VIEW_PACKAGE_MODAL.TIME_SLOT_REASSIGN_DATE", {
                                        oldDate: item.reassignOldDate ? moment(item.reassignOldDate).locale(rtl ? 'ar-SA' : 'en-US').format("MM/DD/YYYY hh:mm A") : ' _ ',
                                        newDate: item.reassignNewDate ? moment(item.reassignNewDate).locale(rtl ? 'ar-SA' : 'en-US').format("MM/DD/YYYY hh:mm A") : ' _ '
                                    })}
                                </div>
                            }
                            {item.event === EventLogName.Ticket_Reassigned && item.isSPReassigned &&
                                <div className='text-muted'>
                                    {t("VIEW_PACKAGE_MODAL.SP_REASSIGN_NAME", {
                                        oldSPName: !item.oldSPFullNameEn && !item.newSPFullNameEn ? '_' : `${rtl ? item.oldSPFullNameAr : item.oldSPFullNameEn}`,
                                        newSPName: !item.newSPFullNameEn && !item.newSPFullNameAr ? '_' : `${rtl ? item.newSPFullNameAr : item.newSPFullNameEn}`,
                                    })}
                                </div>
                            }
                            {/* Assigned worker */}
                            {item.event === EventLogName.Worker_Assigned && !item.oldWorkerFullNameEn && !item.oldWorkerFullNameAr &&
                                <div className='text-muted'>
                                    {t("VIEW_PACKAGE_MODAL.WORKER_ASSIGNED", {
                                        workerName: !item.newWorkerFullNameEn && !item.newWorkerFullNameAr ? '_' : `${rtl ? item.newWorkerFullNameAr : item.newWorkerFullNameEn}`,
                                    })}
                                </div>
                            }
                            {/* Reassigned worker */}
                            {item.event === EventLogName.Worker_Assigned && item.oldWorkerFullNameEn && item.oldWorkerFullNameAr &&
                                item.newWorkerFullNameEn && item.newWorkerFullNameAr &&
                                <div className='text-muted'>
                                    {t("VIEW_PACKAGE_MODAL.WORKER_ASSIGNED_NAME", {
                                        oldWorkerName: !item.oldWorkerFullNameEn && !item.oldWorkerFullNameAr ? '_' : `${rtl ? item.oldWorkerFullNameAr : item.oldWorkerFullNameEn}`,
                                        newWorkerName: !item.newWorkerFullNameEn && !item.newWorkerFullNameAr ? '_' : `${rtl ? item.newWorkerFullNameAr : item.newWorkerFullNameEn}`,
                                    })}
                                </div>
                            }
                            {/* Unassigned worker */}
                            {item.event === EventLogName.Worker_UnAssigned && item.oldWorkerFullNameEn && item.oldWorkerFullNameAr &&
                                <div className='text-muted'>
                                    {t("VIEW_PACKAGE_MODAL.WORKER_ASSIGNED", {
                                        workerName: !item.oldWorkerFullNameEn && !item.oldWorkerFullNameAr ? '_' : `${rtl ? item.oldWorkerFullNameAr : item.oldWorkerFullNameEn}`
                                    })}
                                </div>
                            }
                            {/* Multi unassigned worker */}
                            {item.event === EventLogName.Worker_Multi_UnAssigned && item.workerNameEn && item.workerNameAr &&
                                <div className='text-muted'>
                                    {(() => {
                                        const workerNames = !item.workerNameEn && !item.workerNameAr
                                            ? '_'
                                            : (rtl ? item.workerNameAr : item.workerNameEn);

                                        if (workerNames === '_') {
                                            return '';
                                        }

                                        const nameList = workerNames.split(',').map(name => name.trim()).filter(name => name);
                                        return nameList.map((name, idx) => (
                                            <React.Fragment key={idx}>
                                                {t("VIEW_PACKAGE_MODAL.WORKER_ASSIGNED", { workerName: name })}
                                                <br />
                                            </React.Fragment>
                                        ));
                                    })()}
                                </div>
                            }
                            {/* Worker picked up */}
                            {item.event === EventLogName.Worker_Picked_Up && worker && worker.length > 0 &&
                                <div className='text-muted'>
                                    {worker.length === 1 ? (
                                        t("VIEW_PACKAGE_MODAL.WORKER_ASSIGNED", {
                                            workerName: rtl ? worker[0].fullNameAr : worker[0].fullNameEn
                                        })
                                    ) : (
                                        worker.map((w, idx) => (
                                            <React.Fragment key={idx}>
                                                {t("VIEW_PACKAGE_MODAL.WORKER_ASSIGNED", {
                                                    workerName: rtl ? w.fullNameAr : w.fullNameEn
                                                })}
                                                {idx < worker.length - 1 && <br />}
                                            </React.Fragment>
                                        ))
                                    )}
                                </div>
                            }

                            {/* Rated */}
                            {item.source !== EventLogSource.CApp && item.event === EventLogName.Ticket_Rated && worker && worker.length > 0 &&
                                <div className='text-muted'>
                                    {worker.length === 1 ? (
                                        t("VIEW_PACKAGE_MODAL.WORKER_ASSIGNED", {
                                            workerName: rtl ? worker[0].fullNameAr : worker[0].fullNameEn
                                        })
                                    ) : (
                                        worker.map((w, idx) => (
                                            <React.Fragment key={idx}>
                                                {t("VIEW_PACKAGE_MODAL.WORKERS_ASSIGNED", {
                                                    workerName: rtl ? w.fullNameAr : w.fullNameEn
                                                })}
                                                {idx < worker.length - 1 && <br />}
                                            </React.Fragment>
                                        ))
                                    )}
                                </div>
                            }
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export { PackageTimeLine }
